/**
 * A small WYSIWYG editor for admin-authored portal content — currently just
 * the General settings' Description, but written generically enough for the
 * next place BlueView needs formatted text with no backend of its own.
 *
 * Built on `contentEditable` + `document.execCommand` rather than a rich-text
 * library: every dependency already in this app is either tiny and
 * single-purpose (lucide icons) or something the map genuinely needs
 * (Leaflet). A rich-text engine is neither, and `execCommand` — deprecated,
 * but still implemented everywhere Chromium/Firefox run — covers exactly the
 * handful of commands an admin actually asked for: bold/italic/underline,
 * font, colour, lists, alignment, a link, an image, and a raw-HTML escape
 * hatch for anything this toolbar does not cover.
 *
 * The toolbar buttons use `onMouseDown` + `preventDefault()`, not `onClick`:
 * a click on anything outside the editable region blurs it FIRST, which
 * drops the text selection `execCommand` needs to act on. Stopping the
 * mousedown from taking focus away is what keeps "select some words, click
 * Bold" working at all.
 */

import { useEffect, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Redo2,
  Underline,
  Undo2,
} from 'lucide-react';
import { resizeImage } from '../../utils/imageResize';

const FONT_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Sans', value: '"Segoe UI", Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", Times, serif' },
  { label: 'Mono', value: '"Cascadia Code", Consolas, "Courier New", monospace' },
];

const SIZE_OPTIONS: { label: string; execValue: string }[] = [
  { label: 'Small', execValue: '2' },
  { label: 'Normal', execValue: '3' },
  { label: 'Large', execValue: '5' },
  { label: 'Huge', execValue: '7' },
];

const btnClass =
  'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-200 disabled:opacity-40';

export default function RichTextEditor({
  value,
  onChange,
  hint,
}: {
  /** HTML, as produced by the editor — or typed by hand in Source view. */
  value: string;
  onChange: (html: string) => void;
  hint?: string;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  /* Keeps the editable DOM in step with an EXTERNAL change (Cancel, Reset) —
     never with the user's own typing, which already IS the DOM and would
     have its cursor thrown to the start on every keystroke if this fired for
     it too. Skipped whenever the region is the thing currently focused. */
  useEffect(() => {
    const node = contentRef.current;
    if (!node || sourceMode) return;
    if (document.activeElement === node) return;
    if (node.innerHTML !== (value || '')) node.innerHTML = value || '';
  }, [value, sourceMode]);

  const syncFromDom = () => onChange(contentRef.current?.innerHTML ?? '');

  const exec = (command: string, commandValue?: string) => {
    contentRef.current?.focus();
    document.execCommand(command, false, commandValue);
    syncFromDom();
  };

  const insertLink = () => {
    const url = window.prompt('Link URL:', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const insertImage = async (file: File | undefined) => {
    setImageError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageError('Choose an image file.');
      return;
    }
    setImageBusy(true);
    try {
      // Small target: this is one image inside a block of text, not a hero
      // background — the whole description shares one storage ceiling.
      const resized = await resizeImage(file, { maxDimension: 900, targetBytes: 90 * 1024 });
      exec('insertImage', resized.dataUrl);
    } catch (failure) {
      setImageError(failure instanceof Error ? failure.message : 'That image could not be used.');
    } finally {
      setImageBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="overflow-hidden rounded-lg border border-slate-200">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
          <select
            disabled={sourceMode}
            onChange={(event) => {
              if (event.target.value) exec('fontName', event.target.value);
              event.target.value = '';
            }}
            defaultValue=""
            aria-label="Font"
            className="h-7 rounded-md border border-transparent bg-transparent px-1 text-[11px] text-slate-600 outline-none hover:bg-slate-200 disabled:opacity-40"
          >
            <option value="" disabled>
              Font
            </option>
            {FONT_OPTIONS.filter((option) => option.value).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            disabled={sourceMode}
            onChange={(event) => {
              if (event.target.value) exec('fontSize', event.target.value);
              event.target.value = '';
            }}
            defaultValue=""
            aria-label="Text size"
            className="h-7 rounded-md border border-transparent bg-transparent px-1 text-[11px] text-slate-600 outline-none hover:bg-slate-200 disabled:opacity-40"
          >
            <option value="" disabled>
              Size
            </option>
            {SIZE_OPTIONS.map((option) => (
              <option key={option.execValue} value={option.execValue}>
                {option.label}
              </option>
            ))}
          </select>

          <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('bold');
            }}
            aria-label="Bold"
            title="Bold"
            className={btnClass}
          >
            <Bold className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('italic');
            }}
            aria-label="Italic"
            title="Italic"
            className={btnClass}
          >
            <Italic className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('underline');
            }}
            aria-label="Underline"
            title="Underline"
            className={btnClass}
          >
            <Underline className="h-3.5 w-3.5" />
          </button>

          <label
            className={`flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-md transition hover:bg-slate-200 ${sourceMode ? 'pointer-events-none opacity-40' : ''}`}
            title="Text colour"
          >
            <span
              aria-hidden="true"
              className="h-3.5 w-3.5 rounded-full border border-slate-300"
              style={{ background: 'linear-gradient(135deg,#EF4444,#3B82F6)' }}
            />
            <input
              type="color"
              disabled={sourceMode}
              onChange={(event) => exec('foreColor', event.target.value)}
              aria-label="Text colour"
              className="sr-only"
            />
          </label>

          <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('insertUnorderedList');
            }}
            aria-label="Bulleted list"
            title="Bulleted list"
            className={btnClass}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('insertOrderedList');
            }}
            aria-label="Numbered list"
            title="Numbered list"
            className={btnClass}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </button>

          <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('justifyLeft');
            }}
            aria-label="Align left"
            title="Align left"
            className={btnClass}
          >
            <AlignLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('justifyCenter');
            }}
            aria-label="Align centre"
            title="Align centre"
            className={btnClass}
          >
            <AlignCenter className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('justifyRight');
            }}
            aria-label="Align right"
            title="Align right"
            className={btnClass}
          >
            <AlignRight className="h-3.5 w-3.5" />
          </button>

          <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              insertLink();
            }}
            aria-label="Insert link"
            title="Insert link"
            className={btnClass}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode || imageBusy}
            onMouseDown={(event) => {
              event.preventDefault();
              fileInputRef.current?.click();
            }}
            aria-label="Insert image"
            title="Insert image"
            className={btnClass}
          >
            {imageBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              void insertImage(event.target.files?.[0]);
              event.target.value = '';
            }}
          />

          <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('undo');
            }}
            aria-label="Undo"
            title="Undo"
            className={btnClass}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={sourceMode}
            onMouseDown={(event) => {
              event.preventDefault();
              exec('redo');
            }}
            aria-label="Redo"
            title="Redo"
            className={btnClass}
          >
            <Redo2 className="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            onClick={() => setSourceMode((current) => !current)}
            aria-pressed={sourceMode}
            title="View / edit raw HTML"
            className={`ml-auto flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition ${
              sourceMode ? 'bg-slate-700 text-white' : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Code2 className="h-3.5 w-3.5" />
            HTML
          </button>
        </div>

        {/* Editing surface — WYSIWYG or raw HTML, never both at once. */}
        {sourceMode ? (
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            rows={8}
            spellCheck={false}
            className="w-full resize-none bg-slate-900 px-3 py-2 font-mono text-[12px] text-teal-300 outline-none"
          />
        ) : (
          <div
            ref={contentRef}
            contentEditable
            onInput={syncFromDom}
            onBlur={syncFromDom}
            className="prose-sm min-h-[140px] max-h-[360px] w-full overflow-y-auto px-3 py-2 text-sm text-slate-800 outline-none [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
          />
        )}
      </div>

      {imageError && <p className="text-[11px] text-red-600">{imageError}</p>}
      {hint && !imageError && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}
